/**
 * Rob Steins bulk add: quantity steppers on product cards plus the floating "build your bracelet" bar.
 *
 *  <rs-qty>       rendered by blocks/rs-qty.liquid inside a product card. Picks a quantity for one variant.
 *  <rs-bulk-bar>  rendered once by snippets/rs-bulk-bar.liquid. Shows everything picked so far, progress toward the
 *                 promotions, and adds it all to the cart in one request.
 *
 * Picks live in sessionStorage so they survive filtering, infinite scroll and page navigation.
 * The stepper shows what will be in the bag (already in the bag + picked). "-" lowers the picks first and then what is already
 * in the bag (changed in the cart a moment later), so a charm added earlier can be taken out right from its card.
 * Adding to the cart uses the same standard events and section rendering as Horizon's own product form,
 * so the cart drawer, cart count and cart page update natively.
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { getPromoConfig, promoState, hintText, formatMoney, fetchCart } from '@rs/promo';

const STORAGE_KEY = 'rs-bulk';
const CHANGE_EVENT = 'rs:bulk-change';
const HOLD_DELAY = 400;
const HOLD_INTERVAL = 90;
const MAX_QUANTITY = 99;
const CHAIN_LIMIT = 9;

/**
 * @typedef {Object} Pick
 * @property {number} id         Variant id
 * @property {number} productId
 * @property {number} quantity
 * @property {number} price      Unit price in cents
 * @property {string} title
 * @property {string} image
 */

/** @type {Map<number, Pick>} */
const picks = new Map();

/** @type {Map<number, number>} product id -> quantity in the cart (filled by syncBagMarks below) */
const inBag = new Map();
/** @type {Map<number, number>} product id -> quantity that was just added and is not in `inBag` yet (keeps the stepper steady) */
const recentlyAdded = new Map();
/** True once the cart has been read. Until then a stepper shows the quantity the server rendered into it (data-in-bag). */
let bagLoaded = false;
/** The cart's lines as last read (see syncBagMarks). */
let cartLines = /** @type {any[]} */ ([]);
/** Product ids with a bag change waiting to be sent or on its way: the cart's answers must not overwrite what the customer sees. */
const bagBusy = new Set();
const BAG_EVENT = 'rs:bag-change';

function load() {
  try {
    const raw = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '[]');
    for (const pick of raw) {
      if (pick && pick.id && pick.quantity > 0) picks.set(Number(pick.id), pick);
    }
  } catch {
    /* storage unavailable or corrupt: start empty */
  }
}

function persist() {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify([...picks.values()]));
  } catch {
    /* private mode etc.: picks just won't survive navigation */
  }
}

/**
 * @param {Pick} pick
 * @param {number} quantity
 */
function setPick(pick, quantity) {
  const q = Math.max(0, Math.min(MAX_QUANTITY, Math.floor(quantity) || 0));
  if (q > 0) picks.set(pick.id, { ...pick, quantity: q });
  else picks.delete(pick.id);
  persist();
  document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { id: pick.id, quantity: q } }));
}

function clearPicks() {
  const ids = [...picks.keys()];
  picks.clear();
  persist();
  for (const id of ids) document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { id, quantity: 0 } }));
  document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { id: null, quantity: 0 } }));
}

load();

/* ---- changing what is already in the bag from a card ---- */
/** @type {Map<number, { quantity: number, productId: number }>} variant id -> the quantity the customer wants in the bag, waiting to be sent */
const pendingBag = new Map();
/** @type {ReturnType<typeof setTimeout> | undefined} */
let pendingBagTimer;
/** Cart changes go out one after the other, so a late answer can never show an older state. */
let bagChain = Promise.resolve();

/**
 * Sets how many of a product are in the bag. The card updates at once; the cart is changed a moment later, so holding "-"
 * only sends the final number.
 * @param {number} variantId
 * @param {number} productId
 * @param {number} quantity
 */
function setBagQuantity(variantId, productId, quantity) {
  const q = Math.max(0, Math.floor(quantity) || 0);
  if (q > 0) inBag.set(productId, q);
  else inBag.delete(productId);
  applyBagMarks(document);
  document.dispatchEvent(new CustomEvent(BAG_EVENT));
  pendingBag.set(variantId, { quantity: q, productId });
  bagBusy.add(productId);
  clearTimeout(pendingBagTimer);
  pendingBagTimer = setTimeout(flushBag, 350);
}

function flushBag() {
  bagChain = bagChain.then(async () => {
    const jobs = [...pendingBag.entries()];
    pendingBag.clear();
    for (const [variantId, job] of jobs) await sendBagChange(variantId, job.quantity);
    // these products are settled (unless the customer already pressed again)
    const stillPending = new Set([...pendingBag.values()].map((pending) => pending.productId));
    for (const [, job] of jobs) if (!stillPending.has(job.productId)) bagBusy.delete(job.productId);
    await syncBagMarks(false);
  });
  return bagChain;
}

/** Leaving the page (or hiding the tab) right after pressing "-": send what is waiting now, it would be lost otherwise. */
function flushBagNow() {
  if (!pendingBag.size) return;
  clearTimeout(pendingBagTimer);
  const jobs = [...pendingBag.entries()];
  pendingBag.clear();
  for (const [variantId, job] of jobs) {
    try {
      const plan = planBagChange(cartLines, variantId, job.quantity);
      if (plan) {
        fetch(Theme.routes.cart_update_url, {
          method: 'POST',
          keepalive: true,
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates: plan.updates }),
        });
      }
    } catch {
      /* the page is going away anyway */
    }
    bagBusy.delete(job.productId);
  }
}
window.addEventListener('pagehide', flushBagNow);
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') flushBagNow();
});

/**
 * The "buy 2, get 1 free" offer splits the same charm into several cart lines (3 charms = a line of 2 and a line of 1), so lowering
 * the quantity works on all of them: the first lines keep their quantity and the last ones give up the difference.
 * @param {any[]} items  the cart's lines
 * @param {number} variantId
 * @param {number} quantity  how many of this variant should be left
 * @returns {{ updates: Record<string, number>, changed: { id: string, quantity: number }[] } | null}
 */
function planBagChange(items, variantId, quantity) {
  const lines = items.filter((item) => Number(item.variant_id) === variantId);
  const current = lines.reduce((sum, item) => sum + item.quantity, 0);
  if (!lines.length || quantity >= current) return null;
  let remaining = quantity;
  /** @type {Record<string, number>} */
  const updates = {};
  /** @type {{ id: string, quantity: number }[]} */
  const changed = [];
  for (const line of lines) {
    const keep = Math.min(line.quantity, remaining);
    remaining -= keep;
    if (keep !== line.quantity) {
      updates[line.key] = keep;
      changed.push({ id: line.key, quantity: keep });
    }
  }
  return { updates, changed };
}

/**
 * @param {number} variantId
 * @param {number} quantity
 */
async function sendBagChange(variantId, quantity) {
  let cart;
  try {
    cart = await fetchCart();
  } catch {
    return;
  }
  const plan = planBagChange(cart.items, variantId, quantity);
  if (!plan) return;

  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
    .filter(Boolean)
    .join(',');
  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: quantity > 0 ? 'update' : 'remove',
      context: 'cart',
      lines: plan.changed,
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(Theme.routes.cart_update_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: plan.updates, sections: sectionIds }),
    });
    const json = await response.json();
    if (json.status || json.errors) {
      document.dispatchEvent(
        new CartErrorEvent({
          error: json.message || 'Cart update failed',
          code: 'INVALID',
          detail: { description: json.description, errors: json.errors },
        })
      );
      const fresh = await fetchCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(fresh),
        detail: { didError: true, items: fresh.items, source: 'rs-bulk', sourceId: 'rs-bulk', itemCount: 0 },
      });
      return;
    }
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(json),
      detail: { items: json.items, source: 'rs-bulk', sourceId: 'rs-bulk', itemCount: json.item_count, sections: json.sections, didError: false },
    });
  } catch (error) {
    console.error(error);
    deferred.reject(error);
    document.dispatchEvent(
      new CartErrorEvent({ error: error?.message || 'Network error updating the cart', code: 'SERVICE_UNAVAILABLE' })
    );
  }
}

/**
 * Quantity stepper for one variant.
 * Attributes: data-variant-id, data-product-id, data-price (cents), data-title, data-image, data-max
 */
class RsQty extends HTMLElement {
  static observedAttributes = ['data-variant-id'];

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #holdTimer;
  /** @type {ReturnType<typeof setInterval> | undefined} */
  #holdRepeat;
  /** The "-" button that is pressed down to take something out of the bag (acted on when the tap is finished). */
  #bagTap = /** @type {Element | null} */ (null);

  connectedCallback() {
    // First paint: the server already rendered how many are in the bag; use it until the cart has been read.
    if (!bagLoaded) {
      const seed = Number(this.dataset.inBag) || 0;
      if (seed > 0 && !inBag.has(this.#productId)) {
        inBag.set(this.#productId, seed);
        const card = this.closest('product-card');
        if (card) {
          card.toggleAttribute('data-rs-in-cart', true);
          const label = card.querySelector('[data-rs-bag]');
          if (label) label.textContent = `In your bag · ${seed}`;
        }
      }
    }
    this.addEventListener('pointerdown', this.#onPointerDown);
    this.addEventListener('click', this.#onClick);
    this.addEventListener('pointercancel', this.#cancelBagTap);
    this.addEventListener('pointerleave', this.#onPointerLeave);
    this.addEventListener('keydown', this.#onKeyDown);
    this.addEventListener('change', this.#onChange);
    this.addEventListener('focusin', this.#onFocusIn);
    ['pointerup', 'pointercancel', 'pointerleave'].forEach((type) => this.addEventListener(type, this.#stopHold));
    document.addEventListener(CHANGE_EVENT, this.#sync);
    document.addEventListener(BAG_EVENT, this.#sync);
    this.#sync();
  }

  disconnectedCallback() {
    this.removeEventListener('pointerdown', this.#onPointerDown);
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('pointercancel', this.#cancelBagTap);
    this.removeEventListener('pointerleave', this.#onPointerLeave);
    this.removeEventListener('keydown', this.#onKeyDown);
    this.removeEventListener('change', this.#onChange);
    this.removeEventListener('focusin', this.#onFocusIn);
    ['pointerup', 'pointercancel', 'pointerleave'].forEach((type) => this.removeEventListener(type, this.#stopHold));
    document.removeEventListener(CHANGE_EVENT, this.#sync);
    document.removeEventListener(BAG_EVENT, this.#sync);
    this.#stopHold();
  }

  attributeChangedCallback() {
    if (this.isConnected) this.#sync();
  }

  get #variantId() {
    return Number(this.dataset.variantId);
  }

  get #max() {
    const max = Number(this.dataset.max);
    return Number.isFinite(max) && max > 0 ? Math.min(max, MAX_QUANTITY) : MAX_QUANTITY;
  }

  get #input() {
    return this.querySelector('.rs-qty__input');
  }

  /** What was picked here and is not in the bag yet. */
  get #quantity() {
    return picks.get(this.#variantId)?.quantity ?? 0;
  }

  get #productId() {
    return Number(this.dataset.productId);
  }

  /** What is already in the bag (plus anything that was just added and is still being confirmed). */
  get #bag() {
    return (inBag.get(this.#productId) || 0) + (recentlyAdded.get(this.#productId) || 0);
  }

  /** @returns {Pick} */
  #pick(quantity = this.#quantity) {
    return {
      id: this.#variantId,
      productId: Number(this.dataset.productId),
      quantity,
      price: Number(this.dataset.price) || 0,
      title: this.dataset.title || '',
      image: this.dataset.image || '',
    };
  }

  /** @param {number} quantity  How many to pick (on top of what is already in the bag). */
  #set(quantity) {
    setPick(this.#pick(), Math.max(0, Math.min(quantity, this.#max - this.#bag)));
  }

  /** @param {number} delta  +1 picks one more; -1 un-picks, then takes one out of the bag. */
  #step(delta) {
    if (delta > 0) {
      this.#set(this.#quantity + 1);
    } else if (this.#quantity > 0) {
      this.#set(this.#quantity - 1);
    } else if (this.#bag > 0) {
      const next = this.#bag - 1;
      recentlyAdded.delete(this.#productId);
      setBagQuantity(this.#variantId, this.#productId, next);
    }
  }

  #sync = () => {
    const quantity = this.#quantity;
    const total = quantity + this.#bag;
    const input = this.#input;
    if (input instanceof HTMLInputElement && document.activeElement !== input) input.value = String(total);

    const minus = this.querySelector('[data-step="-1"]');
    const plus = this.querySelector('[data-step="1"]');
    if (minus instanceof HTMLButtonElement) minus.disabled = total <= 0;
    if (plus instanceof HTMLButtonElement) plus.disabled = total >= this.#max;

    this.classList.toggle('rs-qty--active', quantity > 0);
    const card = this.closest('product-card');
    if (card) card.toggleAttribute('data-rs-selected', quantity > 0);
  };

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-step]') : null;
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    const step = Number(button.dataset.step);

    // Taking something out of the bag is deliberate: it waits for a finished tap (so touching the button to scroll never removes
    // anything) and it never repeats while held.
    if (step < 0 && this.#quantity === 0 && this.#bag > 0) {
      this.#bagTap = button;
      return;
    }

    event.preventDefault(); // keeps focus/selection stable and avoids double taps zooming on iOS
    const apply = () => this.#step(step);
    apply();
    this.#stopHold();
    this.#holdTimer = setTimeout(() => {
      this.#holdRepeat = setInterval(apply, HOLD_INTERVAL);
    }, HOLD_DELAY);
  };

  #stopHold = () => {
    clearTimeout(this.#holdTimer);
    clearInterval(this.#holdRepeat);
  };

  #cancelBagTap = () => {
    this.#bagTap = null;
  };

  /** A mouse that leaves before the button is released cancels the tap. (A touch pointer always "leaves" right after it is lifted, before the click, so it must not.) */
  #onPointerLeave = (/** @type {PointerEvent} */ event) => {
    if (event.pointerType === 'mouse') this.#bagTap = null;
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-step]') : null;
    if (!button || this.#bagTap !== button) return;
    this.#bagTap = null;
    if (button instanceof HTMLButtonElement && !button.disabled) this.#step(-1);
  };

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-step]') : null;
    if (button && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      this.#step(Number(button.getAttribute('data-step')));
    }
  };

  /** @param {Event} event */
  #onChange = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;
    // the typed number is how many the customer wants in the bag in total
    const value = parseInt(event.target.value, 10);
    const wanted = Number.isFinite(value) ? Math.max(0, value) : 0;
    const bag = this.#bag;
    if (wanted >= bag) {
      this.#set(wanted - bag);
    } else {
      this.#set(0);
      recentlyAdded.delete(this.#productId);
      setBagQuantity(this.#variantId, this.#productId, wanted);
    }
    this.#sync();
  };

  /** @param {FocusEvent} event */
  #onFocusIn = (event) => {
    if (event.target instanceof HTMLInputElement) event.target.select();
  };
}

/**
 * The floating bar. Markup (chain, count, hint, buttons) is rendered by snippets/rs-bulk-bar.liquid.
 */
class RsBulkBar extends HTMLElement {
  #busy = false;
  /** @type {import('@rs/promo').PromoItem[]} */
  #cartItems = [];
  #lastCount = 0;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #messageTimer;

  connectedCallback() {
    document.addEventListener(CHANGE_EVENT, this.#render);
    document.addEventListener('shopify:cart:lines-update', this.#onCartUpdate);
    window.addEventListener('pageshow', this.#onPageShow);
    this.querySelector('[data-rs-clear]')?.addEventListener('click', clearPicks);
    this.querySelector('[data-rs-add]')?.addEventListener('click', this.#add);
    this.#loadCart();
    this.#render();
  }

  disconnectedCallback() {
    document.removeEventListener(CHANGE_EVENT, this.#render);
    document.removeEventListener('shopify:cart:lines-update', this.#onCartUpdate);
    window.removeEventListener('pageshow', this.#onPageShow);
    clearTimeout(this.#messageTimer);
  }

  #onPageShow = (/** @type {PageTransitionEvent} */ event) => {
    if (!event.persisted) return;
    picks.clear();
    load();
    this.#loadCart();
    document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { id: null, quantity: 0 } }));
  };

  async #loadCart() {
    try {
      this.#cartItems = (await fetchCart()).items;
    } catch {
      this.#cartItems = [];
    }
    this.#render();
  }

  /** @param {Event} event */
  #onCartUpdate = (event) => {
    if (!(event instanceof CartLinesUpdateEvent)) return;
    event.promise?.then(() => this.#loadCart()).catch(() => {});
  };

  #render = () => {
    const list = [...picks.values()];
    const count = list.reduce((sum, pick) => sum + pick.quantity, 0);
    const subtotal = list.reduce((sum, pick) => sum + pick.price * pick.quantity, 0);
    this.hidden = count === 0;
    document.documentElement.toggleAttribute('data-rs-bulk-open', count > 0);
    if (count === 0) {
      this.#lastCount = 0;
      return;
    }

    const config = getPromoConfig();
    const state = promoState([...this.#cartItems, ...list], config);

    const countEl = this.querySelector('[data-rs-count]');
    const hintEl = this.querySelector('[data-rs-hint]');
    const addEl = this.querySelector('[data-rs-add]');
    if (countEl) {
      // the free pieces come off the price shown (the full price stays, crossed out)
      const saved = this.#freePicks(list).saved;
      const pieces = `${count} ${count === 1 ? 'piece' : 'pieces'} · `;
      if (saved > 0) {
        const was = document.createElement('s');
        was.textContent = formatMoney(subtotal, config);
        countEl.replaceChildren(pieces, was, ` ${formatMoney(subtotal - saved, config)}`);
      } else {
        countEl.textContent = pieces + formatMoney(subtotal, config);
      }
    }
    if (hintEl && !this.#messageTimer) hintEl.textContent = hintText(state, config);
    if (addEl && !this.#busy) addEl.textContent = `Add ${count} to cart`;

    const fill = this.querySelector('[data-rs-fill]');
    if (fill instanceof HTMLElement) fill.style.width = `${Math.min(100, (state.subtotal / config.tier2.at) * 100)}%`;
    this.querySelectorAll('[data-rs-mark]').forEach((mark) => {
      if (!(mark instanceof HTMLElement)) return;
      const tier = mark.dataset.rsMark === '2' ? config.tier2 : config.tier1;
      mark.style.left = `${(tier.at / config.tier2.at) * 100}%`;
      mark.classList.toggle('is-hit', state.subtotal >= tier.at);
    });

    this.#renderTiers(state, config);
    this.#renderChain(list, count);
    this.#lastCount = count;
  };

  /**
   * Buy 2, get 1 free: Shopify makes the cheapest pieces free, one for every `bogoSize` pieces in the bag (what is
   * already in the cart counts too). Returns how many of each pick are free and what that takes off the selection.
   * @param {Pick[]} list
   * @returns {{ byPick: Map<number, number>, saved: number }}
   */
  #freePicks(list) {
    const bogoSize = Number(getPromoConfig().bogoSize) || 3;
    const cartUnits = this.#cartItems.flatMap((item) =>
      Array.from({ length: item.quantity }, () => ({ price: item.original_price ?? item.price ?? 0, id: 0 }))
    );
    const pickUnits = list.flatMap((pick) => Array.from({ length: pick.quantity }, () => ({ price: pick.price, id: pick.id })));
    const freeCount = Math.floor((cartUnits.length + pickUnits.length) / bogoSize);
    const byPick = new Map();
    let saved = 0;
    [...cartUnits, ...pickUnits]
      .sort((x, y) => x.price - y.price || x.id - y.id)
      .slice(0, freeCount)
      .forEach((unit) => {
        if (!unit.id) return;
        byPick.set(unit.id, (byPick.get(unit.id) || 0) + 1);
        saved += unit.price;
      });
    return { byPick, saved };
  }

  /**
   * The next goal only: the offer(s) at the next amount to reach (free shipping and 15% off share one, 20% off and the
   * gift card share the next), with what is left to get there. A reached goal goes away and the next one takes its
   * place; when everything is reached a single "all unlocked" tile stays.
   * @param {ReturnType<typeof promoState>} state
   * @param {any} config
   */
  #renderTiers(state, config) {
    const holder = this.querySelector('[data-rs-tiers]');
    if (!holder) return;
    const money = (/** @type {number} */ cents) => formatMoney(cents, config).replace(/.00$/, '');
    const tiers = [
      config.shipping > 0 ? { at: config.shipping, label: 'Free shipping' } : null,
      { at: config.tier1.at, label: `${config.tier1.pct}% off` },
      { at: config.tier2.at, label: `${config.tier2.pct}% off` },
      config.reward > 0 ? { at: config.reward, label: '$10 gift card' } : null,
    ].filter(Boolean);
    const open = tiers.filter((tier) => state.subtotal < tier.at);
    // every goal reached: the whole bar changes look (see .rs-bulk--complete)
    this.classList.toggle('rs-bulk--complete', open.length === 0);
    const li = document.createElement('li');
    const name = document.createElement('b');
    const note = document.createElement('small');
    if (open.length) {
      const nextAt = Math.min(...open.map((tier) => tier.at));
      li.className = 'rs-bulk__tier is-next';
      name.textContent = open.filter((tier) => tier.at === nextAt).map((tier) => tier.label).join(' + ');
      note.textContent = `${money(nextAt - state.subtotal)} to go`;
    } else {
      li.className = 'rs-bulk__tier is-hit';
      name.textContent = 'All offers unlocked';
      note.textContent = '✓';
    }
    li.append(name, note);
    // a new goal pops in
    if (holder.dataset.goal !== name.textContent) {
      holder.dataset.goal = name.textContent || '';
      li.classList.add('is-new');
    }
    holder.replaceChildren(li);
  }

  /**
   * @param {Pick[]} list
   * @param {number} count
   */
  #renderChain(list, count) {
    const chain = this.querySelector('[data-rs-chain]');
    if (!chain) return;

    const units = list.flatMap((pick) => Array.from({ length: Math.min(pick.quantity, CHAIN_LIMIT) }, () => pick));
    const shown = units.slice(0, CHAIN_LIMIT);
    const grew = count > this.#lastCount;
    // how many of each pick are free (see #freePicks); the first ones of that pick in the row carry the label
    const freeLeft = new Map(this.#freePicks(list).byPick);
    const freeIndexes = new Set();
    units.forEach((pick, index) => {
      const left = freeLeft.get(pick.id) || 0;
      if (left > 0) {
        freeIndexes.add(index);
        freeLeft.set(pick.id, left - 1);
      }
    });

    chain.replaceChildren(
      ...shown.map((pick, index) => {
        // each charm is a button: tapping it takes one of that product out of the selection
        const charm = document.createElement('button');
        charm.type = 'button';
        charm.className = 'rs-bulk__charm';
        if (grew && index === shown.length - 1) charm.classList.add('is-new');
        if (freeIndexes.has(index)) {
          charm.classList.add('is-free');
          const free = document.createElement('em');
          free.className = 'rs-bulk__free';
          free.textContent = 'Free';
          charm.append(free);
        }
        charm.title = `Remove ${pick.title}`;
        charm.setAttribute('aria-label', `Remove ${pick.title} from your selection`);
        charm.addEventListener('click', () => {
          const current = picks.get(pick.id);
          if (current) setPick(current, current.quantity - 1);
        });
        const cross = document.createElement('i');
        cross.className = 'rs-bulk__x';
        cross.setAttribute('aria-hidden', 'true');
        cross.textContent = '×';
        charm.append(cross);
        if (pick.image) {
          const img = document.createElement('img');
          img.src = pick.image;
          img.alt = '';
          img.loading = 'lazy';
          img.width = 44;
          img.height = 44;
          charm.append(img);
        }
        return charm;
      })
    );

    if (count > CHAIN_LIMIT) {
      const more = document.createElement('span');
      more.className = 'rs-bulk__more';
      more.textContent = `+${count - CHAIN_LIMIT}`;
      chain.append(more);
    }
  }

  /** @param {string} message */
  #flash(message) {
    const hintEl = this.querySelector('[data-rs-hint]');
    if (!hintEl) return;
    hintEl.textContent = message;
    clearTimeout(this.#messageTimer);
    this.#messageTimer = setTimeout(() => {
      this.#messageTimer = undefined;
      this.#render();
    }, 5000);
  }

  #add = async () => {
    const list = [...picks.values()];
    if (!list.length || this.#busy) return;

    const addEl = this.querySelector('[data-rs-add]');
    this.#busy = true;
    if (addEl instanceof HTMLButtonElement) {
      addEl.disabled = true;
      addEl.textContent = 'Adding…';
    }

    const totalQuantity = list.reduce((sum, pick) => sum + pick.quantity, 0);
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: list.map((pick) => ({ merchandiseId: String(pick.id), quantity: pick.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: list.map((pick) => ({ id: pick.id, quantity: pick.quantity })),
          sections: sectionIds.join(','),
        }),
      });
      const json = await response.json();

      if (json.status) {
        this.dispatchEvent(
          new CartErrorEvent({
            error: json.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: json.description, errors: json.errors },
          })
        );
        const cart = await fetchCart();
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { didError: true, items: cart.items, source: 'rs-bulk-bar', sourceId: 'rs-bulk-bar', itemCount: totalQuantity },
        });
        this.#flash(json.description || json.message || 'Something went wrong. Please try again.');
        return;
      }

      const cart = await fetchCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'rs-bulk-bar',
          sourceId: 'rs-bulk-bar',
          itemCount: totalQuantity,
          sections: json.sections,
          didError: false,
        },
      });
      for (const pick of list) recentlyAdded.set(pick.productId, (recentlyAdded.get(pick.productId) || 0) + pick.quantity);
      setTimeout(() => {
        recentlyAdded.clear();
        document.dispatchEvent(new CustomEvent(BAG_EVENT));
      }, 6000);
      clearPicks();
    } catch (error) {
      console.error(error);
      deferred.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({ error: error?.message || 'Network error during add to cart', code: 'SERVICE_UNAVAILABLE' })
      );
      this.#flash('We couldn’t reach the cart. Please try again.');
    } finally {
      this.#busy = false;
      if (addEl instanceof HTMLButtonElement) addEl.disabled = false;
      this.#render();
    }
  };
}

if (!customElements.get('rs-qty')) customElements.define('rs-qty', RsQty);
if (!customElements.get('rs-bulk-bar')) customElements.define('rs-bulk-bar', RsBulkBar);

/* -------------------------------------------------------------------------------------------------
 * "In your bag": cards for products that are in the cart turn a soft pink and show how many are in
 * the bag. When something is added (from anywhere: the stepper bar, Horizon's quick add, the product
 * page), that card celebrates with a pop, a glow and a burst of sparkles. Styles: blocks/rs-qty.liquid.
 * ----------------------------------------------------------------------------------------------- */
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/** @param {Element} card */
function celebrate(card) {
  card.classList.remove('rs-added');
  void /** @type {HTMLElement} */ (card).offsetWidth; // restart the animation
  card.classList.add('rs-added');
  setTimeout(() => card.classList.remove('rs-added'), 1400);
  if (reduceMotion.matches) return;

  const host = card.querySelector('.product-card__content') || card;
  const count = 10;
  for (let i = 0; i < count; i++) {
    const spark = document.createElement('i');
    spark.className = 'rs-spark';
    const angle = (Math.PI * 2 * i) / count + Math.random() * 0.5;
    const distance = 50 + Math.random() * 44;
    spark.style.setProperty('--dx', `${Math.cos(angle) * distance}px`);
    spark.style.setProperty('--dy', `${Math.sin(angle) * distance}px`);
    spark.style.left = '50%';
    spark.style.top = '40%';
    host.append(spark);
    setTimeout(() => spark.remove(), 1000);
  }
}

/**
 * @param {ParentNode} root
 * @param {Set<number>} [justAdded] product ids that just went up in the cart
 */
function applyBagMarks(root, justAdded = new Set()) {
  for (const card of root.querySelectorAll('product-card[data-product-id]')) {
    const id = Number(card instanceof HTMLElement ? card.dataset.productId : 0);
    const quantity = inBag.get(id) || 0;
    card.toggleAttribute('data-rs-in-cart', quantity > 0);
    const label = card.querySelector('[data-rs-bag]');
    const text = quantity > 0 ? `In your bag · ${quantity}` : '';
    if (label && label.textContent !== text) label.textContent = text;
    if (justAdded.has(id)) celebrate(card);
  }
}

/** @param {boolean} animate */
async function syncBagMarks(animate) {
  let cart;
  try {
    cart = await fetchCart();
  } catch {
    return;
  }

  cartLines = cart.items;

  /** @type {Map<number, number>} */
  const next = new Map();
  for (const item of cart.items) {
    const id = Number(item.product_id);
    next.set(id, (next.get(id) || 0) + item.quantity);
  }

  // products the customer just changed keep what is on screen until their change has been sent
  for (const id of bagBusy) {
    const local = inBag.get(id) || 0;
    if (local > 0) next.set(id, local);
    else next.delete(id);
  }

  const justAdded = new Set();
  if (animate) for (const [id, quantity] of next) if (quantity > (inBag.get(id) || 0)) justAdded.add(id);

  inBag.clear();
  for (const [id, quantity] of next) inBag.set(id, quantity);
  recentlyAdded.clear();
  bagLoaded = true;
  applyBagMarks(document, justAdded);
  document.dispatchEvent(new CustomEvent(BAG_EVENT));
}

syncBagMarks(false);

document.addEventListener('shopify:cart:lines-update', (event) => {
  if (!(event instanceof CartLinesUpdateEvent)) return;
  event.promise?.then(() => syncBagMarks(true)).catch(() => {});
});

window.addEventListener('pageshow', (event) => {
  if (event.persisted) syncBagMarks(false);
});

// New cards appear as the collection loads more products or filters change.
/** @type {ReturnType<typeof setTimeout> | undefined} */
let markTimer;
new MutationObserver(() => {
  clearTimeout(markTimer);
  markTimer = setTimeout(() => applyBagMarks(document), 150);
}).observe(document.body, { childList: true, subtree: true });
